import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, CheckCircle, XCircle, Info } from "lucide-react";
import { format, isValid } from "date-fns";
const safeDate = (v: unknown) => { if (!v) return "—"; const d = new Date(v as string); return isValid(d) ? format(d, "dd MMM yyyy") : "—"; };
import { auditLog } from "@/utils/auditLogger";

interface PatientStipendTrackerProps {
  siteId: string;
}

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pending", variant: "outline" },
  approved: { label: "Approved", variant: "secondary" },
  paid: { label: "Paid", variant: "default" },
  rejected: { label: "Rejected", variant: "destructive" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

const TYPE_LABELS: Record<string, string> = {
  visit: "Visit Stipend",
  travel: "Travel",
  meal: "Meal",
  accommodation: "Accommodation",
  other: "Other",
};

export function PatientStipendTracker({ siteId }: PatientStipendTrackerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: stipends = [], isLoading } = useQuery({
    queryKey: ["patient-stipends", siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_stipends" as any)
        .select("*")
        .eq("trial_site_id", siteId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, receiptFile }: { id: string; status: string; receiptFile?: File }) => {
      const updates: Record<string, any> = { status };
      const { data: user } = await supabase.auth.getUser();
      if (status === "approved") {
        updates.approved_by = user.user?.id;
        updates.approved_at = new Date().toISOString();
      }
      if (status === "paid") {
        if (!receiptFile) throw new Error("A receipt file is required before marking as paid.");
        const path = `${siteId}/${id}/${Date.now()}-${receiptFile.name}`;
        const { error: upErr } = await supabase.storage
          .from("stipend-receipts")
          .upload(path, receiptFile, { upsert: false, contentType: receiptFile.type });
        if (upErr) throw new Error(`Receipt upload failed: ${upErr.message}`);
        updates.receipt_storage_path = path;
        updates.paid_by = user.user?.id;
        updates.paid_at = new Date().toISOString();
      }
      const previous = stipends.find((s: any) => s.id === id);
      const { error } = await supabase.from("patient_stipends" as any).update(updates).eq("id", id);
      if (error) throw error;
      if (previous) {
        await auditLog.reimbursementStatusChanged(id, previous.status, status, {
          patientId: previous.patient_id,
          siteId: previous.trial_site_id,
          trialId: previous.trial_id || null,
          amount: Number(previous.amount) || 0,
          currency: previous.currency,
          previous: {
            status: previous.status ?? null,
            amount: previous.amount != null ? Number(previous.amount) : null,
            currency: previous.currency ?? null,
            approved_by: previous.approved_by ?? null,
            approved_at: previous.approved_at ?? null,
            paid_by: previous.paid_by ?? null,
            paid_at: previous.paid_at ?? null,
          },
          next: {
            status,
            amount: previous.amount != null ? Number(previous.amount) : null,
            currency: previous.currency ?? null,
            approved_by: updates.approved_by ?? previous.approved_by ?? null,
            approved_at: updates.approved_at ?? previous.approved_at ?? null,
            paid_by: updates.paid_by ?? previous.paid_by ?? null,
            paid_at: updates.paid_at ?? previous.paid_at ?? null,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-stipends", siteId] });
      toast({ title: "Status updated" });
    },
    onError: (err: Error) => toast({ title: "Update failed", description: err.message, variant: "destructive" }),
  });

  const filtered = useMemo(
    () => (statusFilter === "all" ? stipends : stipends.filter((s: any) => s.status === statusFilter)),
    [stipends, statusFilter],
  );

  const totalPending = stipends.filter((s: any) => s.status === "pending").reduce((sum: number, s: any) => sum + Number(s.amount), 0);
  const totalPaid = stipends.filter((s: any) => s.status === "paid").reduce((sum: number, s: any) => sum + Number(s.amount), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Patient Stipend &amp; Reimbursement
            </CardTitle>
            <CardDescription>
              Review, approve, and pay stipends generated from completed patient visits.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">{stipends.length}</p>
            <p className="text-xs text-muted-foreground">Total Records</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">{totalPending.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Pending Amount</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-600">{totalPaid.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Total Paid</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">{stipends.filter((s: any) => s.status === "approved").length}</p>
            <p className="text-xs text-muted-foreground">Awaiting Payment</p>
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border rounded-md p-3 mb-4">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Stipends are added from a patient's <strong>Clinical → Visits</strong> page when a visit is completed.
            This page is for review, approval, and payment only.
          </span>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading stipends...</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">
            {stipends.length === 0
              ? "No stipends recorded yet. Complete a patient visit to generate one."
              : "No stipends match this filter."}
          </p>
        ) : (
          <div className="space-y-3">
            {filtered.map((s: any) => {
              const cfg = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
              return (
                <div key={s.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{s.currency} {Number(s.amount).toFixed(2)}</span>
                        <Badge variant={cfg.variant}>{cfg.label}</Badge>
                        <Badge variant="outline" className="text-xs">{TYPE_LABELS[s.stipend_type] || s.stipend_type}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span>Created: {safeDate(s.created_at)}</span>
                        {s.payment_method && <span>Via: {s.payment_method.replace("_", " ")}</span>}
                        {s.paid_at && <span>Paid: {safeDate(s.paid_at)}</span>}
                      </div>
                      {s.notes && <p className="text-xs text-muted-foreground mt-1">{s.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      {s.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => updateStatusMutation.mutate({ id: s.id, status: "approved" })}>
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => updateStatusMutation.mutate({ id: s.id, status: "rejected" })}>
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                      {s.status === "approved" && (
                        <label className="inline-flex items-center">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) updateStatusMutation.mutate({ id: s.id, status: "paid", receiptFile: f });
                              e.currentTarget.value = "";
                            }}
                          />
                          <span className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90">
                            <DollarSign className="h-3 w-3 mr-1" /> Upload receipt &amp; mark paid
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
